import { inject, Injectable } from '@angular/core';
import { Settings } from '../models/settings.base';
import { Order } from '../models/order.model';
import { CallasSettings } from '../models/settings-callas.model';
import { MultipressSettings } from '../models/settings-multipress.model';
import { OktoSettings } from '../models/settings-okto.model';
import { TransportSettings } from '../models/settings-transport.model';
import { BehaviorSubject } from 'rxjs';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  Firestore, getDoc,
  getDocs,
  onSnapshot,
  query,
  Query,
  setDoc,
  Unsubscribe,
  where
} from '@angular/fire/firestore';
import { SiteModel } from '../models/site.model';
import { BusinessUnitModel } from '../models/business-unit.model';

@Injectable({
  providedIn: 'root',
})
/**
 * Order service
 */
export class SettingsService {
  private firestore = inject(Firestore);
  private _unsubscribe: { [key: string]: Unsubscribe } = {};
  private sites$: BehaviorSubject<SiteModel[]>;
  private unsubscribeSites: Unsubscribe;
  private businessUnits$: BehaviorSubject<BusinessUnitModel[]>;
  private unsubscribeBusinessUnits: Unsubscribe;

  /**
   * Subscribe to organization sites
   * @param organizationId The organization id
   * @returns Sites subscription
   */
  public sites(organizationId: string): BehaviorSubject<SiteModel[]> {
    if (this.unsubscribeSites) {
      this.unsubscribeSites();
    }
    this.sites$ = new BehaviorSubject<SiteModel[]>([]);
    const q = query(collection(this.firestore, 'sites'),
      where('organizationId', '==', organizationId)).withConverter(SiteModel.converter);
    this.unsubscribeSites = onSnapshot(q, snapshot => {
      if (snapshot.empty) {
        this.sites$.next([]);
      }
      this.sites$.next(snapshot.docs.map(doc => doc.data()));
    });
    return this.sites$;
  }

  /**
   * Stateless query of organization sites, for callers that manage their own subscription
   * @param organizationId The organization id
   * @returns Sites query
   */
  public sitesQuery(organizationId: string): Query<SiteModel> {
    return query(collection(this.firestore, 'sites'),
      where('organizationId', '==', organizationId)).withConverter(SiteModel.converter);
  }

  async createSite(site: SiteModel): Promise<string> {
    const ref = collection(this.firestore, 'sites').withConverter(SiteModel.converter);
    const created = await addDoc(ref, site);
    return created.id
  }

  public upsertSite(site: SiteModel): Promise<void> {
    const col = collection(this.firestore, 'sites');
    const ref = (site.id ? doc(col, site.id) : doc(col)).withConverter(SiteModel.converter);
    return setDoc(ref, site, { merge: true });
  }

  public deleteSite(id: string): Promise<void> {
    return deleteDoc(doc(this.firestore, 'sites', id));
  }

  async defaultSite(organizationId: string): Promise<SiteModel | null> {
    const q = query(collection(this.firestore, 'sites'),
      where('organizationId', '==', organizationId), where('default', '==', true))
      .withConverter(SiteModel.converter);
    return getDocs(q)
      .then(snapshot => {
        if (snapshot.empty) {
          return null;
        }
        return snapshot.docs[0].data();
      });
  }

  public businessUnits(organizationId: string): BehaviorSubject<BusinessUnitModel[]> {
    if (this.unsubscribeBusinessUnits) {
      this.unsubscribeBusinessUnits();
    }
    this.businessUnits$ = new BehaviorSubject<BusinessUnitModel[]>([]);
    const q = query(collection(this.firestore, 'business-units'),
      where('organizationId', '==', organizationId)).withConverter(BusinessUnitModel.converter);
    this.unsubscribeBusinessUnits = onSnapshot(q, snapshot => {
      if (snapshot.empty) {
        this.businessUnits$.next([]);
      }
      this.businessUnits$.next(snapshot.docs.map(doc => doc.data()));
    });
    return this.businessUnits$;
  }

  public upsertBusinessUnit(businessUnit: BusinessUnitModel): Promise<void> {
    const col = collection(this.firestore, 'business-units');
    const ref = (businessUnit.id ? doc(col, businessUnit.id) : doc(col)).withConverter(BusinessUnitModel.converter);
    return setDoc(ref, businessUnit, { merge: true });
  }

  async createBusinessUnit(businessUnit: BusinessUnitModel): Promise<string> {
    const ref = collection(this.firestore, 'business-units').withConverter(BusinessUnitModel.converter);
    const created = await addDoc(ref, businessUnit);
    return created.id
  }

  public deleteBusinessUnit(id: string): Promise<void> {
    return deleteDoc(doc(this.firestore, 'business-units', id));
  }

  /**
   * Save settings
   * @param settings The settings to save
   * @param organizationId The organization id
   * @returns void
   */
  public async save(settings: Settings, organizationId: string): Promise<void> {
    const ref = doc(this.firestore,
      `settings/${organizationId}/${settings.type}/data`)
      .withConverter(settings.converter);
    return setDoc(ref, settings, { merge: true });
  }

  /**
   * Get a settings by id
   * @param organizationId The organization id
   * @returns The settings
   */
  async getTransportSettings(organizationId: string): Promise<TransportSettings> {
    const newSettings = new TransportSettings();
    const ref = doc(this.firestore,
      `settings/${organizationId}/${newSettings.type}/data`)
      .withConverter(newSettings.converter);
    const res = await getDoc(ref);
    if (!res.exists()) {
      return null;
    }
    return res.data();
  }

  /**
   * Get a settings by id
   * @param organizationId The organization id
   * @returns The settings
   */
  async getCallasSettings(organizationId: string): Promise<CallasSettings> {
    const newSettings = new CallasSettings();
    const ref = doc(this.firestore,
      `settings/${organizationId}/${newSettings.type}/data`)
      .withConverter(newSettings.converter);
    const res = await getDoc(ref);
    if (!res.exists()) {
      return null;
    }
    return res.data();
  }

  /**
   * Get Multipress settings by id
   * @param organizationId The organization id
   * @returns The settings
   */
  async getMultipressSettings(organizationId: string): Promise<MultipressSettings> {
    const newSettings = new MultipressSettings();
    const ref = doc(this.firestore,
      `settings/${organizationId}/${newSettings.type}/data`)
      .withConverter(newSettings.converter);
    const res = await getDoc(ref);
    if (!res.exists()) {
      return null;
    }
    return res.data();
  }

  /**
   * Get Multipress settings by id
   * @param organizationId The organization id
   * @returns The settings
   */
  async getOktoSettings(organizationId: string): Promise<OktoSettings> {
    const newSettings = new OktoSettings();
    const ref = doc(this.firestore,
      `settings/${organizationId}/${newSettings.type}/data`)
      .withConverter(newSettings.converter);
    const res = await getDoc(ref);
    if (!res.exists()) {
      return null;
    }
    return res.data();
  }

  /**
   * Get the samba path to this order
   * @param order The order
   * @returns The order files path
   */
  async getSambaOrderPath(order: Order): Promise<string> {
    return this.getCallasSettings(order.organizationId).then(settings => {
      const smbShareLink = settings.workflowSmbShare
        .replace(/^\\\\/, 'smb://')
        .replace(/\\/g, '/');
      const orderPath = settings.workflowPathTemplate
        .replace(/\\/g, '/')
        .replace(/#YEAR#/g, String(order.date.getFullYear()))
        .replace(/#MONTH#/g, `0${order.date.getMonth() + 1}`.slice(-2))
        .replace(/#COMPANY#/g, order.company)
        .replace(/#JOB_NUMBER#/g, order.transactionId);
      return `${smbShareLink}/${orderPath}`;
    });
  }

  /**
   * Check if MultipressSettings is valid
   * @param multipress Multipress settings
   * @returns True if Multipress settings are completed
   */
  isMultipressValid(multipress: MultipressSettings): boolean {
    if (!multipress) {
      return false;
    }
    if (!multipress.url || !multipress.login || !multipress.password) {
      return false;
    }
    let isValid = !!multipress.filesId && !!multipress.scriptId &&
      !!multipress.formatId && !!multipress.contactsId && !!multipress.conditioningId &&
      !!multipress.productionInformationId && !!multipress.supportId && !!multipress.finishingId
      && !!multipress.archiveState;

    const syncOrders = !!multipress.syncOrdersStartId && !!multipress.waitFileState &&
      !!multipress.processingState && !!multipress.waitBatState &&
      !!multipress.batRefusedState && !!multipress.batAcceptedState && !!multipress.printingState;

    if (isValid && multipress.syncOrders) {
      isValid = syncOrders;
    }
    if (isValid && multipress.syncOrders && multipress.planning.updateOutsidePlanning && !multipress.printingState) {
      isValid = false;
    }
    const syncQuotations = !!multipress.syncQuotationStartId && !!multipress.quotationNewState &&
      !!multipress.quotationAcceptedState && !!multipress.quotationRefusedState &&
      !!multipress.quotationValidationState;

    if (isValid && multipress.syncQuotations) {
      isValid = syncQuotations;
    }
    const syncShipments = !!multipress.readyToShipState && !!multipress.shippedState &&
      !!multipress.pickUpDeliveryId && !!multipress.courierDeliveryId && !!multipress.charterDeliveryId;

    if (isValid && multipress.syncShipments) {
      isValid = syncShipments;
    }
    const syncInvoices = !!multipress.syncInvoicesStartId && !!multipress.readyToInvoiceState &&
      !!multipress.unpaidState && !!multipress.setToInvoiceState;

    if (isValid && multipress.syncInvoices) {
      isValid = syncInvoices;
    }
    const syncStock = !!multipress.stockStates.length;

    if (isValid && multipress.automaticStock) {
      isValid = syncStock;
    }
    return isValid
  }

  /**
   * Check if CallasSettings is valid
   * @param callas Callas settings
   * @returns True if callas settings are completed
   */
  isCallasValid(callas: CallasSettings): boolean {
    const required = !!callas.productionFolder && !!callas.paoFolder && !!callas.customerFolder &&
      !!callas.batFolder.length;
    if (callas.agentEnabled) {
      return callas?.workflowDir && callas.workflowPathTemplate && required;
    } else {
      return required;
    }
  }

  unsubscribe(): void {
    if (this.unsubscribeSites) {
      this.unsubscribeSites();
    }
    this.sites$ = null;
    if (this.unsubscribeBusinessUnits) {
      this.unsubscribeBusinessUnits();
    }
    this.sites$ = null;
    for (const unsubscribe of Object.values(this._unsubscribe)) {
      if (unsubscribe) {
        unsubscribe();
      }
      this._unsubscribe = {};
    }
  }
}
